/**
 * GET `{VITE_API_BASE_URL}/jain_lexicon/activity-logs/` — staff JWT + tenant.
 * Query: `page`, `page_size`, `activity_type`, `search`, `start_date`, `end_date`.
 */
import { getStaffAccessToken } from "./authApi";
import { formatApiErrorBody } from "./formatApiError";

export const ACTIVITY_LOGS_PATH = "/jain_lexicon/activity-logs/";

export const ACTIVITY_LOGS_PAGE_SIZE = 20;

export const ACTIVITY_LOG_FETCH_TYPES = ["Edit", "Delete", "Create"] as const;

export type ActivityLogFetchType = (typeof ACTIVITY_LOG_FETCH_TYPES)[number];

export type ActivityLogsQuery = {
  page?: number;
  page_size?: number;
  activity_type?: ActivityLogFetchType;
  search?: string;
  start_date?: string;
  end_date?: string;
};

export type ActivityLogsPagination = {
  count: number;
  page: number;
  page_size: number;
};

function jlApiBase(): string {
  const raw =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_API_BASE_URL as string | undefined)
      : undefined;
  return (raw ?? "").replace(/\/$/, "");
}

function jlTenantHeaders(): Record<string, string> {
  const id =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const trimmed = (id ?? "").replace(/^\uFEFF/, "").trim();
  if (!trimmed) {
    return {};
  }
  return { "X-Tenant-Id": trimmed };
}

function coerceList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    return raw.filter(
      (x): x is Record<string, unknown> =>
        typeof x === "object" && x !== null && !Array.isArray(x)
    );
  }
  if (!raw || typeof raw !== "object") {
    return [];
  }
  const o = raw as Record<string, unknown>;
  for (const key of ["results", "data", "content", "items"] as const) {
    const arr = o[key];
    if (Array.isArray(arr)) {
      return arr.filter(
        (x): x is Record<string, unknown> =>
          typeof x === "object" && x !== null && !Array.isArray(x)
      );
    }
  }
  return [];
}

function coercePositiveInt(v: unknown, fallback: number): number {
  const n =
    typeof v === "number"
      ? v
      : typeof v === "string"
        ? Number.parseInt(v, 10)
        : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

export function parseActivityLogsPagination(
  raw: unknown,
  fallback: { page: number; page_size: number }
): ActivityLogsPagination | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const list = coerceList(raw);
  const countRaw = o.count ?? o.total ?? o.total_count;
  if (countRaw === undefined || countRaw === null) {
    if (list.length === 0) {
      return null;
    }
    return {
      count: list.length,
      page: fallback.page,
      page_size: fallback.page_size,
    };
  }
  return {
    count: coercePositiveInt(countRaw, list.length),
    page: coercePositiveInt(o.page, fallback.page),
    page_size: coercePositiveInt(o.page_size, fallback.page_size),
  };
}

function apiHostLabel(apiBase: string): string {
  try {
    const u = new URL(apiBase.includes("://") ? apiBase : `https://${apiBase}`);
    return u.host;
  } catch {
    return apiBase.replace(/^https?:\/\//i, "").split("/")[0] || apiBase;
  }
}

function networkFetchErrorMessage(apiBase: string, cause: unknown): string {
  const host = apiHostLabel(apiBase);
  const raw =
    cause instanceof Error ? cause.message : String(cause ?? "Network error");
  if (/failed to fetch|load failed|networkerror/i.test(raw)) {
    return `Failed to fetch (${host}). The browser blocked this request — usually CORS or TLS. Ask the API team to allow your site’s origin on OPTIONS + GET for /jain_lexicon/activity-logs/ (Authorization, X-Tenant-Id).`;
  }
  return raw;
}

async function parseHttpError(res: Response): Promise<string> {
  const fallback = `Request failed (${res.status})`;
  try {
    const t = await res.text();
    const trimmed = t.trim();
    if (!trimmed) {
      return fallback;
    }
    try {
      const j = JSON.parse(trimmed) as unknown;
      return formatApiErrorBody(j) ?? fallback;
    } catch {
      return trimmed.length > 300 ? `${trimmed.slice(0, 300)}…` : trimmed;
    }
  } catch {
    return fallback;
  }
}

export function buildActivityLogsUrl(
  base: string,
  query: ActivityLogsQuery
): string {
  const page = query.page ?? 1;
  const pageSize = query.page_size ?? ACTIVITY_LOGS_PAGE_SIZE;
  const qs = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (query.activity_type) {
    qs.set("activity_type", query.activity_type);
  }
  const search = query.search?.trim();
  if (search) {
    qs.set("search", search);
  }
  const start = query.start_date?.trim();
  if (start) {
    qs.set("start_date", start);
  }
  const end = query.end_date?.trim();
  if (end) {
    qs.set("end_date", end);
  }
  const prefix = base.replace(/\/$/, "");
  return `${prefix}${ACTIVITY_LOGS_PATH}?${qs.toString()}`;
}

/**
 * One paginated GET with server-side filters (`search`, `start_date`, `end_date`, `activity_type`).
 */
export async function fetchActivityLogs(
  query: ActivityLogsQuery = {}
): Promise<{
  logs: Record<string, unknown>[];
  error: string | null;
  pagination: ActivityLogsPagination | null;
}> {
  const base = jlApiBase();
  const token = getStaffAccessToken();
  const page = query.page ?? 1;
  const page_size = query.page_size ?? ACTIVITY_LOGS_PAGE_SIZE;

  if (!base) {
    return { logs: [], error: "Missing VITE_API_BASE_URL", pagination: null };
  }
  if (!token) {
    return {
      logs: [],
      error: "Sign in with a staff account to load activity logs.",
      pagination: null,
    };
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...jlTenantHeaders(),
    Authorization: `Bearer ${token}`,
  };

  const url = buildActivityLogsUrl(base, { ...query, page, page_size });

  let res: Response;
  try {
    res = await fetch(url, { method: "GET", headers });
  } catch (e) {
    return {
      logs: [],
      error: networkFetchErrorMessage(base, e),
      pagination: null,
    };
  }

  if (!res.ok) {
    return {
      logs: [],
      error: await parseHttpError(res),
      pagination: null,
    };
  }

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return {
      logs: [],
      error: "Invalid JSON from activity logs API",
      pagination: null,
    };
  }

  const logs = coerceList(raw);
  const pagination =
    parseActivityLogsPagination(raw, { page, page_size }) ??
    (logs.length > 0
      ? { count: logs.length, page, page_size }
      : null);

  return { logs, error: null, pagination };
}

export function getActivityLogsRequestSpec(
  query: ActivityLogsQuery = {
    page: 1,
    page_size: ACTIVITY_LOGS_PAGE_SIZE,
    activity_type: "Create",
  }
): {
  method: "GET";
  url: string;
  headers: Record<string, string>;
} {
  const base = jlApiBase();
  const pathQuery = buildActivityLogsUrl("", query);
  const url = base ? `${base}${pathQuery}` : pathQuery;
  const tid =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const tenant = (tid ?? "").replace(/^\uFEFF/, "").trim();
  return {
    method: "GET",
    url,
    headers: {
      Authorization: "Bearer <staff_access_token>",
      Accept: "application/json",
      "X-Tenant-Id": tenant || "(not set — VITE_X_TENANTID)",
    },
  };
}
